import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Card, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useCallback, useEffect } from "react";
import { numberWithCommas } from "../../../utils/customFunctions";
import ButtonComponent from '../../../components/form/buttonComponent';
import { useNavigate } from "react-router-dom";
import DialogComponent from "../../../components/dialog/dialogComponent";
import SnackbarComponent from "../../../components/snackBar/snackbarComponent";
import { FormProviderComponent, TextFieldComponent } from '../../../components/form';
import InfoIcon from '@mui/icons-material/Info';
import { useDeleteActivityAndSiteMutation, useEditActivityAndSiteMutation, useSaveActivityAndSiteMutation, useGetListOfSitesAndActivitiesMutation } from '../../../services/sitesAndActivities';
import { GetAuth } from '../../../context/auth/authProvider';
import CloseIcon from '@mui/icons-material/Close';
import { GetSitesAndActivityInfo } from '../../../context/calculator/sitesAndActivity/sitesAndActivityProvider';
import { getTotalActivitiesFee, getTotalActivityRowAmount, getTotalAmountActivityRowBreakDown, getTotalAmountSiteRowBreakDown, getTotalFee, getTotalSiteRowAmount, getTotalSitesFee } from './sitesAndActivitiesUtils';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const tableHeadingBgColor = '#ececec';

//Shared
const resultsRow = (label, value, highlight=false, openBrief=null) => {
    return <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
    >
        <Typography
            fontWeight={400}
            fontSize={{xs: highlight ? 16 : 14, md: highlight ? 20 : 18}}
            letterSpacing={0.3}
            color='black'
            textAlign='left'
        >
            {label}
        </Typography>

        <Stack
            direction='row'
            alignItems='center'
            justifyContent='end'
        >
            <Typography
                fontWeight={highlight ? 700 : 400}
                fontSize={{xs: highlight ? 16 : 14, md: highlight ? 20 : 18}}
                letterSpacing={0.36}
                color={highlight ? 'black' : '#5E5959'}
                textAlign='right'
            >
                {value}
            </Typography>

            {
                openBrief
                ?
                <IconButton 
                    aria-label="info" 
                    onClick={openBrief}
                    sx={{
                        p: 0,
                        ml: 0.5
                    }}
                >
                    <InfoIcon 
                        sx={{ 
                            color: '#ea6142',
                            fontSize: 20
                        }}
                    />
                </IconButton>
                :
                <></>
            }
        </Stack>
    </Stack>
}

const CalculatorResults = ({
    backToFilterView,
    summaryData,
    loadAllTours,
    modelViewClose=null,
    sx
}) => {
    const theme = useTheme();

    const navigate = useNavigate();

    const [data, setData] = useState(null);

    const calculator = GetSitesAndActivityInfo();
    const auth = GetAuth();

    const formSchema = yup.object().shape({
        tripName: yup.string().required('Trip name is required')
    });
            
    const defaultValues = {
        tripName: ""
    };
            
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
            
    const {
        setValue,
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;
            
    const values = watch();

    useEffect(() => {
        if(summaryData){
            console.log("Received summary...");
            console.log(summaryData);

            const siteFees = summaryData?.siteFees;
            const needsNameEnrichment = siteFees?.length > 0 && siteFees[0].refId && !siteFees[0].siteName;

            if(!needsNameEnrichment){
                setData({
                    id: summaryData.id,
                    name: summaryData.name,
                    kids: summaryData.kidsCount,
                    adults: summaryData.adultCount,
                    siteList: summaryData.siteFees,
                    activityList: summaryData.activityFees
                });
            }
        }
        else if(calculator){
            console.log("Received shared data...");
            console.log(calculator.calculatorInfo);

            if(calculator.calculatorInfo.id){
                setValue("tripName", calculator.calculatorInfo.name);
            }

            let obj = {...calculator.calculatorInfo, siteList: calculator?.sharedData?.siteList, activityList: calculator?.sharedData?.activities};

            console.log(obj);

            setData(obj);
        }
    }, [summaryData]);

    useEffect(() => {
        if(data){
            console.log("Data...");
            console.log(data);
        }
    }, [data]);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const requestSave = useCallback(() => {
        setShowSaveDialog(true);
    }, []);

    const [
        saveSiteAndActivity,
        {
            isLoading: isLoadingSiteAndActivitySave,
            isSuccess: isSuccessSiteAndActivitySave,
            isError: isErrorSiteAndActivitySave,
            error: errorSiteAndActivitySave 
        }
    ] = useSaveActivityAndSiteMutation();

    const [
        editSiteAndActivity,
        {
            isLoading: isLoadingSiteAndActivityEdit,
            isSuccess: isSuccessSiteAndActivityEdit,
            isError: isErrorSiteAndActivityEdit,
            error: errorSiteAndActivityEdit 
        }
    ] = useEditActivityAndSiteMutation();

    const confirmSave = useCallback(() => {
        console.log(data);

        setShowSaveDialog(false);

        if(data.id){
            const body = {
                "activityFees": data?.activityList,
                "adultCount": data.adults,
                "id": data.id,
                "kidsCount": data.kids,
                "name": values.tripName,
                "sideFees": data?.siteList,
                "staffId": auth?.currentUser?.staffID
            }

            console.log(body);

            editSiteAndActivity({body: body});
        }
        else{
            const body = {
                "activityFees": data?.activityList,
                "adultCount": data.adults,
                "kidsCount": data.kids,
                "name": values.tripName,
                "sideFees": data?.siteList,
                "staffId": auth?.currentUser?.staffID
            }

            console.log(body);

            saveSiteAndActivity({body: body});
        }
    }, [data, calculator, values, auth]);

    useEffect(() => {
        if(isSuccessSiteAndActivitySave){
            setSnackBar({
                message: "Successfully saved!",
                open: true,
                color: "green"
            });

            calculator?.clearCalculator();
            calculator.clearEditSitesAndActivityHistory();

            setTimeout(() => {
                navigate('/records?type=sites_activities');
            }, 1000);
        }
        else if(isErrorSiteAndActivitySave){
            setSnackBar({
                message: errorSiteAndActivitySave?.data?.message ?? "Something went wrong",
                open: true,
                color: "red"
            });
        }
    }, [isErrorSiteAndActivitySave, errorSiteAndActivitySave, isSuccessSiteAndActivitySave]);

    useEffect(() => {
        if(isSuccessSiteAndActivityEdit){
            setSnackBar({
                message: "Successfully updated!",
                open: true,
                color: "green"
            });

            calculator?.clearCalculator();
            calculator.clearEditSitesAndActivityHistory();

            setTimeout(() => {
                navigate('/records?type=sites_activities');
            }, 1000);
        }
        else if(isErrorSiteAndActivityEdit){
            setSnackBar({
                message: errorSiteAndActivityEdit?.data?.message ?? "Something went wrong",
                open: true,
                color: "red"
            });
        }
    }, [isErrorSiteAndActivityEdit, errorSiteAndActivityEdit, isSuccessSiteAndActivityEdit]);

    const deleteRequest = useCallback(() => {
        setShowDeleteDialog(true);
    }, []);

    const [
        getSiteAndActivityList,
        { data: sitesAndActivitiesList }
    ] = useGetListOfSitesAndActivitiesMutation();

    useEffect(() => {
        if(summaryData){
            const siteFees = summaryData?.siteFees;
            const needsNameEnrichment = siteFees?.length > 0 && siteFees[0].refId && !siteFees[0].siteName;

            if(needsNameEnrichment){
                getSiteAndActivityList({staffId: auth?.currentUser?.staffID});
            }
        }
    }, [summaryData]);

    useEffect(() => {
        if(sitesAndActivitiesList && summaryData){
            const sites = sitesAndActivitiesList?.sites || {};
            const activities = sitesAndActivitiesList?.activities || {};

            const enrichedSiteList = (summaryData.siteFees || []).map(fee => ({
                ...fee,
                siteName: sites[fee.refId] || fee.tag || ''
            }));

            const enrichedActivityList = (summaryData.activityFees || []).map(fee => ({
                ...fee,
                activityName: activities[fee.refId] || fee.tag || ''
            }));

            setData({
                id: summaryData.id,
                name: summaryData.name,
                kids: summaryData.kidsCount,
                adults: summaryData.adultCount,
                siteList: enrichedSiteList,
                activityList: enrichedActivityList
            });
        }
    }, [sitesAndActivitiesList]);

    const [
        deleteSiteAndActivity,
        {
            isLoading: isLoadingSiteAndActivityDeletion,
            isSuccess: isSuccessSiteAndActivityDeletion,
            isError: isErrorSiteAndActivityDeletion,
            error: errorSiteAndActivityDeletion 
        }
    ] = useDeleteActivityAndSiteMutation();

    useEffect(() => {
        if(isSuccessSiteAndActivityDeletion){
            setSnackBar({
                message: "Successfully deleted!",
                open: true,
                color: "green"
            });

            setTimeout(() => {
                if(modelViewClose) modelViewClose();
                
                loadAllTours();
            }, 1000);
        }
        else if(isErrorSiteAndActivityDeletion){
            console.log("Tour Delete Error...");
            console.log(errorSiteAndActivityDeletion);

            if(errorSiteAndActivityDeletion.originalStatus === 200){
                setSnackBar({
                    message: "Successfully deleted!",
                    open: true,
                    color: "green"
                });

                setTimeout(() => {
                    if(modelViewClose) modelViewClose();

                    loadAllTours();
                }, 1000);
            }
            else{
                setSnackBar({
                    message: errorSiteAndActivityDeletion?.data?.message ?? "Something went wrong",
                    open: true,
                    color: "red"
                });
            }
        }
    }, [isErrorSiteAndActivityDeletion, errorSiteAndActivityDeletion, isSuccessSiteAndActivityDeletion]);

    const confirmDeleteRequest = useCallback(() => {
        setShowDeleteDialog(false);
        
        deleteSiteAndActivity({id: summaryData.id, staffId: auth?.currentUser?.staffID});
    }, [data, summaryData]);

    const editRequest = useCallback(() => {
        console.log("Edit requested...");
        console.log(summaryData);

        const activityList = [];
        for(let i = 0; i < summaryData.activityFees.length; i++){
            activityList.push(summaryData.activityFees[i].refId);
        }

        const siteList = [];
        for(let i = 0; i < summaryData.siteFees.length; i++){
            siteList.push(summaryData.siteFees[i].refId);
        }

        const obj = {
            id: summaryData.id,
            adultCount: summaryData.adultCount,
            kidsCount: summaryData.kidsCount,
            lastUpdatedOn: summaryData.lastUpdatedOn,
            name: summaryData.name,
            activityFees: activityList,
            siteFees: siteList
        }

        calculator.editSitesAndActivity(obj);

        navigate('/calculator?type=sites_activities');
    }, [summaryData]);

    const backToTripCalculations = useCallback(() => {
        console.log(data);

        calculator.editSitesAndActivity({
            id: data?.id,
            siteFees: data?.siteList,
            activityFees: data?.activityList,
            kidsCount: data?.kids,
            adultCount: data?.adults,
            name: data?.name
        });

        backToFilterView({
            id: data?.id,
            siteFees: data?.sites,
            activityFees: data?.activities,
            kidsCount: data?.kids,
            adultCount: data?.adults,
            name: data?.name
        });
    }, [data]);

    const createANewPlan = useCallback(() => {
        calculator?.clearCalculator();
        calculator?.clearEditSitesAndActivityHistory();

        backToFilterView(null, true);
    }, [calculator]);

    return (
        <>
            <Box 
                px={{xs: summaryData ? 0 : 2, md: summaryData ? 0 : 7}}
                pb={{xs: summaryData ? 0 : 15, md: 0}}
                sx={{
                    maxHeight: '100vh',
                    overflowY: 'scroll',
                    ...sx
                }}
                className="hide-scrollbar"
            >
                <Stack
                    direction='column'
                    justifyContent='start'
                    alignItems='center'
                    width='100%'
                    py={{xs: summaryData ? 0 : 2, md: summaryData ? 0 : 9}}
                    data-aos="zoom-in"
                >
                    {
                        !summaryData
                        ?
                        <>
                            {/* Title */}
                            <Typography
                                fontWeight={800}
                                fontSize={40}
                                letterSpacing={0.36}
                                color={theme.palette.primary.main}
                                textAlign='center'
                                sx={{
                                    display: {xs: 'none', md: 'block'}
                                }}
                            >
                                Site and Activity Rate Calculation
                            </Typography>

                            {/* Back */}
                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                                mt={{xs: 2, md: 4}}
                            >
                                <ButtonComponent 
                                    label='New Plan'
                                    onClick={createANewPlan}
                                    sx={{
                                        width: 'fit-content',
                                        fontWeight: 500,
                                        fontSize: {xs: 12, md: 16},
                                        letterSpacing: 0.36,
                                        p: {xs: 1, md: 1.5}
                                    }}
                                    variant="outlined"
                                    startIcon={<AddCircleIcon />}
                                />
                            </Stack>
                        </>
                        :
                        <></>
                    }

                    {
                        modelViewClose
                        ?
                        <Stack
                            direction='row'
                            alignItems='center'
                            position='absolute'
                            width='100%'
                        >
                            <Stack
                                direction='row'
                                justifyContent='center'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                                position='absolute'
                            >
                                <Typography
                                    fontWeight={600}
                                    color={theme.palette.primary.main}
                                    fontSize={{xs: 16, lg: 20}}
                                >
                                    {data?.name}
                                </Typography>
                            </Stack>

                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                            >
                                <IconButton onClick={modelViewClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        :
                        <></>
                    }

                    <Card 
                        elevation={summaryData ? 0 : 3}
                        sx={{
                            width: '100%',
                            mt: {xs: summaryData ? 0 : 2, md: summaryData ? 0 : 3},
                            pt: {xs: modelViewClose ? 4 : 0, md: modelViewClose ? 8 : 0}
                        }}
                    >
                        <Box sx={{p: {xs: 3, md: 5}}}>
                            <Stack
                                direction='column'
                                justifyContent='start'
                                alignItems='center'
                                width='100%'
                                spacing={{xs: 2.5, md: 3.5}}
                            >
                                {resultsRow('Kids (6-11.99)', data?.kids > 0 ? `${data?.kids < 10 ? `x 0${data?.kids}` : `x ${data?.kids}`}` : 'N/A')}
                                {resultsRow('Adults', data?.adults > 0 ? `${data?.adults < 10 ? `x 0${data?.adults}` : `x ${data?.adults}`}` : 'N/A')}

                                <Box 
                                    sx={{
                                        width: '100%',
                                        py: 1
                                    }}
                                >
                                    <Divider 
                                        sx={{
                                            width: '100%',
                                            border: '0.5px solid #D9D9D9'
                                        }}
                                    />
                                </Box>

                                {/* Sites */}
                                <TableContainer component={Paper} elevation={0}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            <TableRow sx={{backgroundColor: tableHeadingBgColor}}>
                                                <TableCell>Sites</TableCell>
                                                <TableCell align="right">Per Kid&nbsp;($)</TableCell>
                                                <TableCell align="right">Per Adult&nbsp;($)</TableCell>
                                                <TableCell align="right">Total Amount&nbsp;($)</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {
                                                data?.siteList?.length > 0
                                                ?
                                                <>
                                                    {
                                                        data?.siteList?.map((row, index) => (
                                                            <TableRow
                                                                key={index}
                                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                            >
                                                                <TableCell component="th" scope="row">{row.siteName}</TableCell>
                                                                <TableCell align="right">{row.kidsAge612Fee}</TableCell>
                                                                <TableCell align="right">{row.adultFees['1']}</TableCell>
                                                                <TableCell align="right">{
                                                                    getTotalSiteRowAmount(data?.kids, data?.adults, row.kidsAge612Fee, row.adultFees['1'], row.adultFees['9'], row.adultFees['10'])
                                                                }
                                                                <br />
                                                                <span style={{fontSize: 12, color: '#767676'}}>{
                                                                    getTotalAmountSiteRowBreakDown(data?.kids, data?.adults, row.kidsAge612Fee, row.adultFees['1'], row.adultFees['9'], row.adultFees['10'])
                                                                }</span></TableCell>
                                                            </TableRow>
                                                        ))
                                                    }

                                                    {
                                                        <TableRow sx={{backgroundColor: '#fff0ed'}}>
                                                            <TableCell colSpan={3} align="right" sx={{fontWeight: 400, fontSize: {xs: 14, md: 16}}}>Total Sites Fee</TableCell>
                                                            <TableCell align="right" sx={{fontWeight: 600, fontSize: {xs: 14, md: 16}}}>{
                                                                getTotalSitesFee(data?.siteList ?? [], data?.kids, data?.adults) > 0
                                                                ?
                                                                `$ ${numberWithCommas(getTotalSitesFee(data?.siteList ?? [], data?.kids, data?.adults))}`
                                                                :
                                                                `$0`
                                                            }</TableCell>
                                                        </TableRow>
                                                    }
                                                </>
                                                :
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">No Sites Available</TableCell>
                                                </TableRow>
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Activities */}
                                <TableContainer component={Paper} elevation={0}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            <TableRow sx={{backgroundColor: tableHeadingBgColor}}>
                                                <TableCell>Activities</TableCell>
                                                <TableCell align="right">Per Kid&nbsp;($)</TableCell>
                                                <TableCell align="right">Adult Rates&nbsp;($)</TableCell>
                                                <TableCell align="right">Total Amount&nbsp;($)</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {
                                                data?.activityList?.length > 0
                                                ?
                                                <>
                                                    {
                                                        data?.activityList?.map((row, index) => (
                                                            <TableRow
                                                                key={index}
                                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                            >
                                                                <TableCell component="th" scope="row">{row.activityName}</TableCell>
                                                                <TableCell align="right">{row.kidsAge612Fee}</TableCell>
                                                                <TableCell align="right">{
                                                                    Number.parseFloat(row.adultFees['10']) > 0
                                                                    ?
                                                                    `${row.adultFees['1']} - ${row.adultFees['10']}`
                                                                    :
                                                                    Number.parseFloat(row.adultFees['9']) > 0
                                                                    ?
                                                                    `${row.adultFees['1']} - ${row.adultFees['9']}`
                                                                    :
                                                                    `${row.adultFees['1']} - ${row.adultFees['8']}`
                                                                }</TableCell>
                                                                <TableCell align="right">{getTotalActivityRowAmount(data?.kids, data?.adults, row)}<br /><span style={{fontSize: 12, color: '#767676'}}>{getTotalAmountActivityRowBreakDown(data?.kids, data?.adults, row)}</span></TableCell>
                                                            </TableRow>
                                                        ))
                                                    }

                                                    {
                                                        <TableRow sx={{backgroundColor: '#fff0ed'}}>
                                                            <TableCell colSpan={3} align="right" sx={{fontWeight: 400, fontSize: {xs: 14, md: 16}}}>Total Activities Fee</TableCell>
                                                            <TableCell align="right" sx={{fontWeight: 600, fontSize: {xs: 14, md: 16}}}>{
                                                                getTotalActivitiesFee(data?.activityList, data?.kids, data?.adults) > 0
                                                                ?
                                                                `$ ${numberWithCommas(getTotalActivitiesFee(data?.activityList ?? [], data?.kids, data?.adults))}`
                                                                :
                                                                `$0`
                                                            }</TableCell>
                                                        </TableRow>
                                                    }
                                                </>
                                                :
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">No Activities Available</TableCell>
                                                </TableRow>
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Total */}
                                <TableContainer component={Paper} elevation={0}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            <TableRow sx={{backgroundColor: '#292929'}}>
                                                <TableCell colSpan={3} align="right" sx={{color: 'white', fontWeight: 700, fontSize: {xs: 14, md: 20}}}>Total Sites & Activities Fee</TableCell>
                                                <TableCell align="right" sx={{color: 'white', fontWeight: 700, fontSize: {xs: 14, md: 20}}}>{`$ ${numberWithCommas(getTotalFee(data?.siteList ?? [], data?.activityList ?? [], data?.kids, data?.adults))}`}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                    </Table>
                                </TableContainer>
                            </Stack>
                        </Box>
                    </Card>

                    {/* Action */}
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        width='100%'
                        spacing={4}
                        pt={summaryData ? 0 : 6}
                        pb={summaryData ? 2 : 0}
                    >
                        {
                            summaryData
                            ?
                            <Stack 
                                direction='row'
                                justifyContent='center'
                                width='100%'
                                px={{xs: 3, md: 5}}
                                spacing={{xs: 1, md: 2}}
                            >
                                <ButtonComponent 
                                    label='Delete'
                                    fullWidth
                                    onClick={deleteRequest}
                                    inLoading={isLoadingSiteAndActivityDeletion}
                                    variant='outlined'
                                />

                                <ButtonComponent 
                                    label='Edit'
                                    fullWidth
                                    onClick={editRequest}
                                    inLoading={false}
                                />
                            </Stack>
                            :
                            <>
                                <ButtonComponent 
                                    label='Back'
                                    fullWidth
                                    onClick={backToTripCalculations}
                                    variant='outlined'
                                />

                                <ButtonComponent 
                                    label={data?.id ? 'Update' : 'Save'}
                                    fullWidth
                                    onClick={requestSave}
                                    inLoading={isLoadingSiteAndActivitySave || isLoadingSiteAndActivityEdit}
                                />
                            </>
                        }
                    </Stack>
                </Stack>
            </Box>

            {/* Save or Edit */}
            <DialogComponent 
                open={showSaveDialog}
                title={data?.id ? 'Update' : 'Save'}
                body={<>
                    <FormProviderComponent methods={formMethods} onSubmit={()=>{}}>
                        <TextFieldComponent 
                            name='tripName'
                            placeholder="Your plan name here"
                            fullWidth
                        />
                    </FormProviderComponent>
                </>}
                handleClose={() => setShowSaveDialog(false)}
                proceedText={data?.id ? 'Update' : 'Save'}
                cancelText="Cancel"
                proceedCallback={confirmSave}
                cancelCallback={() => setShowSaveDialog(false)}
                disableProceed={values.tripName.trim().length === 0}
            />

            {/* Delete */}
            <DialogComponent 
                open={showDeleteDialog}
                title="Delete"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Typography
                            fontWeight={300}
                            fontSize={14}
                            textAlign='justify'
                        >
                            Are you sure to delete the plan?
                        </Typography>
                    </Stack>
                </>}
                handleClose={() => setShowDeleteDialog(false)}
                proceedText="No"
                cancelText="Yes"
                proceedCallback={() => setShowDeleteDialog(false)}
                cancelCallback={confirmDeleteRequest}
            />

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default CalculatorResults;